import { Inbox, Loader2, SlidersHorizontal, ZapOff } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   "Nothing here" is four different situations, and conflating them wastes
   the reader's time in exactly the moment they are confused. The `reason`
   enum picks the a11y semantics and the default icon:

     empty     nothing exists yet          → tell them how to start
     filtered  it exists, you hid it       → offer to unhide
     pending   it is being produced        → say so, and that nothing is lost
     error     it failed                   → say what failed and offer a retry

   RULE: gate on `isSuccess` before rendering. `data?.length === 0` while a
   query is in flight prints "no results" against a background of pending
   truth.
*/

export type EmptyReason = "empty" | "filtered" | "pending" | "error"

const REASON_ICON: Record<EmptyReason, React.ReactNode> = {
  empty: <Inbox aria-hidden="true" />,
  filtered: <SlidersHorizontal aria-hidden="true" />,
  pending: <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />,
  error: <ZapOff aria-hidden="true" />,
}

export interface EmptyStateProps {
  reason?: EmptyReason
  /** One line. State of the world, not an apology. */
  title: React.ReactNode
  /** One or two sentences. Never just "No data". */
  body?: React.ReactNode
  /** What the reader can do next — a Button, an anchor, or nothing. */
  action?: React.ReactNode
  /** Override the reason's default icon. */
  icon?: React.ReactNode
  /** Strips card chrome so this can drop into a DataTable body. */
  bare?: boolean
  className?: string
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { reason = "empty", title, body, action, icon, bare = false, className },
  ref,
) {
  return (
    <div
      ref={ref}
      role={reason === "error" ? "alert" : undefined}
      aria-busy={reason === "pending" || undefined}
      className={cn(
        "flex flex-col items-center gap-2.5 px-5 py-10 text-center",
        !bare && "rounded-lg border border-dashed border-border",
        reason === "error" && !bare && "border-err/35",
        className,
      )}
    >
      <div className="mb-0.5 text-text-disabled [&_svg]:size-6">{icon ?? REASON_ICON[reason]}</div>
      <h3 className={cn("text-subtitle", reason === "error" && "text-err")}>{title}</h3>
      {body && <p className="max-w-[52ch] text-body-sm leading-normal text-text-2">{body}</p>}
      {action && <div className="mt-0.5 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
})
